import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IncidentSymbol,
  GroupedIncidentSymbol,
  resolveIncidentType,
} from "./incidentSymbols";
import { ratingTier10, formatRating10 } from "../../totw/totwUtils";
import SubstitutionCard from "./SubstitutionCard";

// =====================================================================
// MatchLineup — Bench list (design handoff bench column, boards L1–L11).
//
// Pure presentation, props-driven: provider-shaped player objects, no
// fetch, no app context. Rows are ≥44px, mono shirt-number column,
// photo circle with the shirt-number fallback (L9), name, position
// letter, in-arrow + minute in #2183FE for players who came on, the
// grouped incident symbols and the rating chip (ratingTier10 — the
// single TOTW threshold source).
//
// Sub pairing: the in-arrow cluster on a bench row is the anchor of the
// SubstitutionCard (hover on desktop, tap on mobile — L12.2). All subs
// involving the player (in AND a later off — L12.4) stack in one card.
//
// Mobile: rows are plain full-width flex rows (name truncates, all
// side clusters flex-none) — they stack, nothing scrolls sideways.
//
// Coach line: plain text below the rows (brief §2 "coach line plain
// text"), rendered only when a name is passed — empty renders nothing.
// =====================================================================

// Group repeat incidents of the same resolved symbol (handoff: glyph +
// ×n count). Resolution goes through the shared resolveIncidentType
// (unknown codes → null, already warned there). The substitute-in event
// is carried by the arrow+minute cluster, so a resolved "in" is skipped
// here to avoid a duplicate arrow.
const groupIncidents = (player) => {
  const order = [];
  const byKey = {};
  ((player && player.incidents) || []).forEach((incident) => {
    const key = resolveIncidentType(incident, player);
    if (!key || key === "in") return;
    if (!byKey[key]) {
      byKey[key] = { key, count: 0 };
      order.push(key);
    }
    byKey[key].count += 1;
  });
  // Bench rows cap at three symbol groups (handoff incidentIcons slice).
  return order.slice(0, 3).map((key) => byKey[key]);
};

// 24px photo circle with the shirt-number fallback (L9) — a missing or
// broken photo renders the number, never a broken image.
const BenchPhoto = ({ player }) => {
  const src = (player && player.logo) || null;
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [src]);
  if (src && !errored) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setErrored(true)}
        className="rounded-full object-cover flex-none"
        style={{ width: 24, height: 24, border: "1px solid #E2E8F0" }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex-none flex items-center justify-center"
      style={{
        width: 24,
        height: 24,
        background: "#EDF1F6",
        border: "1px solid #E2E8F0",
        fontSize: 10,
        fontWeight: 700,
        color: "#475569",
      }}
    >
      {player && player.shirt_number != null && player.shirt_number !== ""
        ? player.shirt_number
        : null}
    </div>
  );
};

const hoverCapable = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover)").matches;

const BenchList = ({
  players = [],
  substitutions = [],
  highlightPlayerId = null,
  coachName = "",
  coachId = null,
  onPlayerClick,
  // header label — the XI-as-list fallback (no placement data) reuses this
  // component with "Starting XI"
  title = "Bench",
}) => {
  const navigate = useNavigate();
  // { playerId, anchorEl } of the open substitution card.
  const [openSub, setOpenSub] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenSub(null), 150);
  };

  const activate = (player) => {
    if (onPlayerClick) {
      onPlayerClick(player);
      return;
    }
    if (player && player.platform_player_id != null) {
      navigate(`/search/player/${player.platform_player_id}`);
    }
  };
  const canActivate = (player) =>
    !!player && (!!onPlayerClick || player.platform_player_id != null);

  const subsFor = (playerId) =>
    (substitutions || []).filter(
      (sub) =>
        (sub.inPlayer && sub.inPlayer.id === playerId) ||
        (sub.outPlayer && sub.outPlayer.id === playerId),
    );
  const cameOnSub = (playerId) =>
    (substitutions || []).find((sub) => sub.inPlayer && sub.inPlayer.id === playerId);

  const openCardRows = openSub ? subsFor(openSub.playerId) : [];

  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <div
        className="uppercase"
        style={{
          fontSize: 11,
          letterSpacing: "0.1em",
          color: "#64748B",
          fontWeight: 700,
          padding: "2px 0",
        }}
      >
        {title}
      </div>

      {players.map((player) => {
        const highlighted =
          highlightPlayerId != null &&
          (String(player.id) === String(highlightPlayerId) ||
            (player.platform_player_id != null &&
              String(player.platform_player_id) === String(highlightPlayerId)));
        const groups = groupIncidents(player);
        const onSub = cameOnSub(player.id);
        // Provider ships rating as a STRING (§0 audit) — parse before
        // use; absent / empty / zero renders no chip (no reserved hole).
        const ratingNum = Number(player.rating);
        const hasRating =
          player.rating != null && player.rating !== "" && Number.isFinite(ratingNum) && ratingNum > 0;
        const interactive = canActivate(player);
        const rowNav = interactive
          ? {
              role: "button",
              tabIndex: 0,
              onClick: () => activate(player),
              onKeyDown: (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  activate(player);
                }
              },
            }
          : {};
        const openCard = (event) => {
          cancelClose();
          setOpenSub({ playerId: player.id, anchorEl: event.currentTarget });
        };
        return (
          <div
            key={player.id}
            {...rowNav}
            className={`flex items-center hover:bg-[#F8FAFC] ${
              interactive ? "cursor-pointer" : ""
            }`}
            style={{
              gap: 8,
              minHeight: 44,
              padding: "4px 8px",
              borderRadius: 8,
              background: highlighted ? "rgba(33,131,254,0.08)" : "transparent",
              boxShadow: highlighted
                ? "inset 0 0 0 2px #2183FE, 0 0 14px 2px rgba(33,131,254,0.35)"
                : "none",
            }}
          >
            <div
              className="flex-none text-right"
              data-no-translate="true"
              style={{
                width: 26,
                fontFamily: "ui-monospace,Menlo,monospace",
                fontSize: 12,
                color: "#64748B",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {player.shirt_number}
            </div>
            <BenchPhoto player={player} />
            <div
              className="truncate"
              data-no-translate="true"
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                fontSize: 13,
                fontWeight: 600,
                color: "#0F172A",
              }}
            >
              {player.name || player.first || null}
            </div>
            {player.position ? (
              <div
                className="flex-none"
                data-no-translate="true"
                style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8" }}
              >
                {player.position}
              </div>
            ) : null}
            <div className="ml-auto flex-none flex items-center" style={{ gap: 6 }}>
              {groups.length ? (
                <div className="flex items-center" style={{ gap: 2 }}>
                  {groups.map((group) => (
                    <span key={group.key} className="flex">
                      <GroupedIncidentSymbol
                        type={group.key}
                        count={group.count}
                        size={13}
                      />
                    </span>
                  ))}
                </div>
              ) : null}
              {onSub ? (
                // In-arrow + minute — the anchor of the substitution
                // detail card (hover on desktop, tap everywhere). The
                // cluster keeps a ≥44px hit area without inflating the
                // 44px row.
                <div
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  className="flex items-center justify-center cursor-pointer"
                  style={{ gap: 3, minWidth: 44, minHeight: 44, margin: "-4px 0" }}
                  onMouseEnter={(event) => {
                    if (hoverCapable()) openCard(event);
                  }}
                  onMouseLeave={() => {
                    if (hoverCapable()) scheduleClose();
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (openSub && openSub.playerId === player.id) {
                      setOpenSub(null);
                    } else {
                      openCard(event);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      openCard(event);
                    }
                  }}
                >
                  <span className="flex">
                    <IncidentSymbol type="in" size={13} />
                  </span>
                  {onSub.minute ? (
                    <span
                      data-no-translate="true"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#2183FE",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {onSub.minute}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {hasRating ? (
                <span
                  style={{
                    background: ratingTier10(ratingNum).bg,
                    color: ratingTier10(ratingNum).fg,
                    fontSize: 11,
                    fontWeight: 800,
                    padding: "2px 7px",
                    borderRadius: 6,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatRating10(ratingNum)}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}

      {coachName ? (
        <div
          className={`flex items-center ${coachId ? "cursor-pointer" : ""}`}
          style={{ gap: 6, minHeight: 44, padding: "2px 8px", marginTop: 2, borderRadius: 8 }}
          role={coachId ? "button" : undefined}
          tabIndex={coachId ? 0 : undefined}
          onClick={
            coachId ? () => navigate(`/search/coach/${coachId}`) : undefined
          }
          onKeyDown={
            coachId
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/search/coach/${coachId}`);
                  }
                }
              : undefined
          }
        >
          <div style={{ fontSize: 12, color: "#64748B" }}>Coach</div>
          <div
            data-no-translate="true"
            style={{
              fontSize: 13,
              color: coachId ? "#2183FE" : "#0F172A",
              fontWeight: 600,
            }}
          >
            {coachName}
          </div>
        </div>
      ) : null}

      <SubstitutionCard
        open={!!openSub && openCardRows.length > 0}
        rows={openCardRows}
        anchorEl={openSub ? openSub.anchorEl : null}
        onClose={() => setOpenSub(null)}
        onPlayerClick={onPlayerClick}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      />
    </div>
  );
};

export default BenchList;
